/**
 * finishedActivityAdapter — une activité finie, rouverte, telle que l'API la sert → ce que la page
 * d'une activité finie montre, et le corps de « Refaire l'activité ».
 *
 * Portage littéral de l'ancien `ActivityFinishedScreen` et de sa boîte `ActivityDetailsModal` :
 *  - l'en-tête est celui de l'exécution (`toStudentActivityHeading`) ; le statut est la `DoneChip`
 *    de l'ancien en-tête (`StaticOneStepActivityHeader`), le chrono « 12 / 20 min » ses badges ;
 *  - la copie : les pages envoyées (`submittedWorkImages`), la correction en première image et ses
 *    points (`toCorrectedWork`, la même lecture que la correction qui suit l'envoi) ; pour l'oral,
 *    le premier enregistrement envoyé et la durée posée sur son journal ;
 *  - « Refaire l'activité » : le même corps et le même plafond du jour (`maxReached`).
 *
 * Un écart, assumé : ce que l'élève a rendu (photos ou oral) suit `workMediumOf`, la règle de
 * l'exécution. L'ancien écran lisait « pas d'étape photo → oral », et un « Go exo » rendu en photos
 * s'y relisait comme un oral, sans rien à écouter.
 */
import moment from "moment"

import { submittedWorkImages } from "../../../../../engine/activity/correction/correctionFlow"
import { type WorkMedium } from "../../../../../engine/activity/workMedium"
import {
    type UIActivityDetails,
    type UIActivityStatement,
    type UIActivityStatus,
    type UIActivityWork,
} from "../../../ui/types"
import { toUIActivityStatus } from "../../parent/Activities/activityAdapter"
import { redoActivityBody } from "../../parent/Activities/activityDetailsAdapter"
import { type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"
import { toCorrectedWork, toStudentActivityHeading, type ApiRunActivity } from "./studentActivityAdapter"

/** Ce que l'écran lit d'une activité finie. Formes lâches (`ports/CLAUDE.md` § 5). */
export type ApiFinishedActivity = {
    id?: number | null
    title?: string | null
    school_subject_id?: number | null
    activity_template_id?: number | null
    activityTemplateId?: number | null
    finished_at?: string | null
    correction_status?: string | null
    material?: ApiRunActivity["material"]
    status?: string | null
    duration?: number | null
    estimated_duration?: number | null
    review_comment?: string | null
    correction_image?: string | null
    correction_points?: unknown[] | null
    challenge_id?: number | null
    again_activity_id?: number | null
    chapter?: unknown
    student_chapter?: {
        chapter?: { id?: number | null; name?: string | null; class?: string | null } | null
    } | null
    activity_logs?:
        | {
              action?: string | null
              work_attachment?: string | null
              step_data?: { chrono_duration?: number | null } | null
          }[]
        | null
} & Record<string, unknown>

export type ApiFinishedTemplate = {
    id?: number | null
    parent_type?: string | null
    max_per_day?: number | null
    steps?: { type?: string | null }[] | null
}

export type ApiChallenge = { id?: number | null; planning_mode?: unknown; school_subject?: ApiSchoolSubject | null }

/**
 * Une activité ouverte se relit au lieu de s'exécuter : la règle de l'ancien `ActivityWrapperScreen`
 * pour une activité que le tuteur affiche (« à faire » s'exécute, tout autre statut se relit).
 */
export function isFinishedActivity(activity: { status?: unknown } | null | undefined): boolean {
    return typeof activity?.status === "string" && activity.status !== "todo"
}

/**
 * Le statut de la chip « Fait / Validé », la règle de `DoneChip.js`, la même sur la page d'une
 * activité faite et sur la photo du fil devenue activité : un statut absent (`undefined`) prend sa
 * valeur par défaut, « Fait » (`not_reviewed`) ; tout statut qu'elle ne nomme pas, `null` compris
 * (une valeur par défaut ne joue pas pour `null`), tombe dans sa dernière branche, « Validé ».
 */
export function finishedActivityStatus(status: string | null | undefined): UIActivityStatus {
    return status === undefined ? "not_reviewed" : toUIActivityStatus(status)
}

/** Ce que l'élève a rendu : les photos (la correction en tête), ou l'enregistrement. `null` : rien. */
export function toFinishedWork(
    activity: ApiFinishedActivity | null | undefined,
    medium: WorkMedium,
    cleanUrl: (url: string | null | undefined) => string | null
): UIActivityWork | null {
    if (medium === "audio") {
        const log = (activity?.activity_logs ?? []).find((entry) => entry?.action === "step_media_sent")
        const uri = cleanUrl(log?.work_attachment)
        if (uri == null || uri === "") return null
        return {
            images: [],
            audioUri: uri,
            audioDurationSeconds: log?.step_data?.chrono_duration ?? undefined,
        }
    }
    const work = toCorrectedWork({
        image: activity?.correction_image ?? null,
        points: activity?.correction_points ?? null,
        workImages: submittedWorkImages(activity),
    })
    return work.images.length > 0 ? work : null
}

/** La fiche de la page : l'en-tête, l'énoncé, la copie. */
export function toFinishedActivityDetails(
    activity: ApiFinishedActivity,
    options: {
        subjects: ApiSchoolSubject[] | null | undefined
        statement: UIActivityStatement
        medium: WorkMedium
        cleanUrl: (url: string | null | undefined) => string | null
    }
): UIActivityDetails {
    const heading = toStudentActivityHeading(activity as ApiRunActivity, options.subjects)
    return {
        id: activity.id ?? "",
        title: heading.title,
        subject: heading.subject,
        chapterName: heading.chapterName,
        status: finishedActivityStatus(activity.status),
        estimatedDurationMinutes: activity.estimated_duration ?? undefined,
        // `duration` est en secondes ; l'ancien badge écrit « - » quand elle vaut zéro.
        spentDurationSeconds: activity.duration ? activity.duration : undefined,
        statement: options.statement,
        work: toFinishedWork(activity, options.medium, options.cleanUrl),
    }
}

/** `countSameActivitiesToday` (`data/utility/dataUtils.js`), recopié : le module tire de l'ancien code. */
function countSameActivitiesToday(
    template: ApiFinishedTemplate | null,
    activities: ApiFinishedActivity[],
    challengeId: number | null,
    subjectId: number | string | null
): number {
    return activities
        .filter((x) => x?.activity_template_id === template?.id)
        .filter((x) =>
            template?.parent_type === "challenge" ? x?.challenge_id === challengeId : x?.school_subject_id === subjectId
        )
        .filter((x) => x?.finished_at && moment().isSame(x?.finished_at, "day"))
        .filter((x) => x?.status !== "rejected").length
}

/** `countSameAgainActivities` (`data/utility/dataUtils.js`), recopié. */
function countSameAgainActivities(
    template: ApiFinishedTemplate | null,
    activities: ApiFinishedActivity[],
    againActivityId: number | null | undefined
): number {
    const activity = activities.find((x) => x?.id === againActivityId)
    if (!activity || !activity.finished_at) return 0
    return (
        1 +
        activities
            .filter((x) => x?.activity_template_id === template?.id)
            .filter((x) => x?.again_activity_id === againActivityId)
            .filter((x) => !!x?.finished_at)
            .filter((x) => x?.status !== "rejected").length
    )
}

export type FinishedRedo = {
    /** « Refaire l'activité » ne s'offre pas : l'activité n'est pas finie, ou le plafond du jour est atteint. */
    offered: boolean
    body: Record<string, unknown>
}

/**
 * « Refaire l'activité » (`ActivityFinishedScreen.onRetry`) : le corps de `activityTodoAsStudent`, et
 * le plafond du jour du modèle (`max_per_day`), compté comme l'ancien écran, sur la seule activité
 * ouverte. Le bouton ne s'offre que sur une activité finie, plafond non atteint (`showRetryButton`).
 */
export function finishedRedo(
    activity: ApiFinishedActivity,
    context: {
        templates: ApiFinishedTemplate[] | null | undefined
        subjects: ApiSchoolSubject[] | null | undefined
        challenges: ApiChallenge[] | null | undefined
    }
): FinishedRedo {
    const templateId = activity.activity_template_id ?? activity.activityTemplateId
    const template = (context.templates ?? []).find((t) => t?.id === templateId) ?? null
    const challenge =
        activity.challenge_id != null
            ? ((context.challenges ?? []).find((c) => c?.id === activity.challenge_id) ?? null)
            : null
    const subject =
        activity.school_subject_id != null
            ? ((context.subjects ?? []).find((s) => s?.id === activity.school_subject_id) ?? null)
            : (challenge?.school_subject ?? null)

    const activities = [activity]
    const sameCount = challenge?.planning_mode
        ? countSameAgainActivities(template, activities, activity.id)
        : countSameActivitiesToday(template, activities, challenge?.id ?? null, subject?.id ?? null)
    // `sameActivitiesCount >= activityTemplate?.max_per_day`, au caractère près : un plafond absent
    // (`undefined`) ne bloque rien, un plafond `null` vaut 0 en JavaScript, et bloque donc toujours.
    const cap = template?.max_per_day
    const maxReached = cap === undefined ? false : sameCount >= (cap ?? 0)

    return {
        offered: !!activity.finished_at && !maxReached,
        body: redoActivityBody(activity as never, {
            activityTemplateId: template?.id ?? undefined,
            schoolSubjectId: subject?.id ?? undefined,
        }),
    }
}
